import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Ticket, Plus, Trash2, Copy } from "lucide-react";
import { format } from "date-fns";

interface PromoCoupon {
  id: string;
  code: string;
  discount_amount: number;
  discount_type: string;
  max_uses: number | null;
  times_used: number;
  valid_from: string;
  expires_at: string;
  is_used: boolean;
  reason: string;
  created_at: string;
}

const PromotionalCoupons = () => {
  const [coupons, setCoupons] = useState<PromoCoupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  // Form state
  const [code, setCode] = useState("");
  const [discountType, setDiscountType] = useState<"fixed" | "percent">("fixed");
  const [discountAmount, setDiscountAmount] = useState("10");
  const [maxUses, setMaxUses] = useState("");
  const [validFrom, setValidFrom] = useState(format(new Date(), "yyyy-MM-dd"));
  const [validUntil, setValidUntil] = useState(
    format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd")
  );
  const [reason, setReason] = useState("Promotional");

  const fetchCoupons = async () => {
    const { data, error } = await supabase
      .from("coupons")
      .select("*")
      .eq("is_promotional", true)
      .order("created_at", { ascending: false });

    if (error) {
      toast({ title: "Error loading coupons", description: error.message, variant: "destructive" });
    } else {
      setCoupons((data || []) as PromoCoupon[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const generateCode = () => {
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    setCode(`PROMO-${random}`);
  };

  const handleCreate = async () => {
    if (!code.trim()) {
      toast({ title: "Code required", description: "Enter or generate a coupon code", variant: "destructive" });
      return;
    }
    const amount = parseFloat(discountAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({ title: "Invalid amount", description: "Discount must be greater than 0", variant: "destructive" });
      return;
    }
    if (discountType === "percent" && amount > 100) {
      toast({ title: "Invalid percentage", description: "Percentage cannot exceed 100", variant: "destructive" });
      return;
    }

    setCreating(true);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({ title: "Not authenticated", variant: "destructive" });
      setCreating(false);
      return;
    }

    const insertData: any = {
      code: code.trim().toUpperCase(),
      discount_amount: amount,
      discount_type: discountType,
      max_uses: maxUses ? parseInt(maxUses) : null,
      valid_from: new Date(validFrom).toISOString(),
      expires_at: new Date(validUntil + "T23:59:59").toISOString(),
      reason: reason || "Promotional",
      is_promotional: true,
      user_id: user.id,
    };

    const { error } = await supabase.from("coupons").insert(insertData);

    if (error) {
      toast({ title: "Error creating coupon", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Coupon created", description: `${insertData.code} is now active` });
      setCode("");
      setDiscountAmount("10");
      setMaxUses("");
      setReason("Promotional");
      fetchCoupons();
    }
    setCreating(false);
  };

  const handleDelete = async (id: string, code: string) => {
    if (!confirm(`Delete coupon "${code}"? This cannot be undone.`)) return;
    const { error } = await supabase.from("coupons").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setCoupons((prev) => prev.filter((c) => c.id !== id));
      toast({ title: "Coupon deleted" });
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({ title: "Copied", description: code });
  };

  const isExpired = (c: PromoCoupon) => new Date(c.expires_at) < new Date();
  const isMaxedOut = (c: PromoCoupon) => c.max_uses !== null && c.times_used >= c.max_uses;
  const isActive = (c: PromoCoupon) => !isExpired(c) && !isMaxedOut(c) && new Date(c.valid_from) <= new Date();

  return (
    <Card className="bg-card border-border/50 max-w-3xl">
      <CardHeader>
        <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
          <Ticket size={16} />
          Promotional Coupons
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-muted-foreground text-sm font-light">
          Create promo codes clients can apply at checkout. Set a usage cap and a date window.
        </p>

        <div className="border border-border/50 bg-background p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs tracking-wide uppercase text-muted-foreground">Code</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="SUMMER20"
                  className="bg-card border-border/50 uppercase"
                />
                <Button type="button" variant="outline" onClick={generateCode} className="text-xs shrink-0">
                  Generate
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-xs tracking-wide uppercase text-muted-foreground">Reason / Label</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Summer Sale"
                className="bg-card border-border/50 mt-1"
              />
            </div>

            <div>
              <Label className="text-xs tracking-wide uppercase text-muted-foreground">Discount Type</Label>
              <Select value={discountType} onValueChange={(v) => setDiscountType(v as "fixed" | "percent")}>
                <SelectTrigger className="bg-card border-border/50 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed ($)</SelectItem>
                  <SelectItem value="percent">Percentage (%)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs tracking-wide uppercase text-muted-foreground">
                Amount {discountType === "fixed" ? "($)" : "(%)"}
              </Label>
              <Input
                type="number"
                min={1}
                step="0.01"
                value={discountAmount}
                onChange={(e) => setDiscountAmount(e.target.value)}
                className="bg-card border-border/50 mt-1"
              />
            </div>

            <div>
              <Label className="text-xs tracking-wide uppercase text-muted-foreground">Max Uses (blank = unlimited)</Label>
              <Input
                type="number"
                min={1}
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                placeholder="Unlimited"
                className="bg-card border-border/50 mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">Valid From</Label>
                <Input
                  type="date"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  className="bg-card border-border/50 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">Expires</Label>
                <Input
                  type="date"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  className="bg-card border-border/50 mt-1"
                />
              </div>
            </div>
          </div>

          <Button onClick={handleCreate} disabled={creating} className="text-xs tracking-widest uppercase">
            <Plus size={14} className="mr-2" />
            {creating ? "Creating…" : "Create Coupon"}
          </Button>
        </div>

        <div className="space-y-2">
          <h3 className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Active &amp; Past Coupons</h3>
          {loading ? (
            <p className="text-muted-foreground text-sm font-light">Loading…</p>
          ) : coupons.length === 0 ? (
            <p className="text-muted-foreground text-sm font-light">No promotional coupons yet.</p>
          ) : (
            <div className="space-y-2">
              {coupons.map((c) => {
                const active = isActive(c);
                const expired = isExpired(c);
                const maxed = isMaxedOut(c);
                return (
                  <div
                    key={c.id}
                    className={`p-3 border bg-background flex items-center justify-between gap-3 ${
                      active ? "border-primary/40" : "border-border/30 opacity-70"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-bold text-primary">{c.code}</span>
                        <button
                          onClick={() => copyCode(c.code)}
                          className="text-muted-foreground hover:text-foreground"
                          title="Copy code"
                        >
                          <Copy size={12} />
                        </button>
                        <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground">
                          {c.discount_type === "percent"
                            ? `${c.discount_amount}% off`
                            : `$${c.discount_amount} off`}
                        </span>
                        {active && <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary">Active</span>}
                        {expired && <span className="text-xs px-2 py-0.5 bg-destructive/20 text-destructive">Expired</span>}
                        {maxed && !expired && (
                          <span className="text-xs px-2 py-0.5 bg-destructive/20 text-destructive">Max used</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {c.reason} · Used {c.times_used}
                        {c.max_uses !== null ? `/${c.max_uses}` : ""} ·{" "}
                        {format(new Date(c.valid_from), "MMM d")} – {format(new Date(c.expires_at), "MMM d, yyyy")}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(c.id, c.code)}
                      className="text-destructive shrink-0"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PromotionalCoupons;
